import AppButton from '@nxsys/components/AppButton';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useSaveImportAgencyById,
  useSaveImportEmployeeById,
  useSaveImportProspectById,
} from '@nxsys/hooks/api/import';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { showMessage } from '@nxsys/utils/message';
import { ColumnsType } from 'antd/es/table';
import React, { useEffect, useMemo, useState } from 'react';
import UploadedFileInformation from './UploadedFileInformation';
import { IMPORT_COLUMNS_WIDTH, IMPORT_SCREEN_TYPE } from '../constants';

type ImportResultUploadModalProps = {
  isOpen: boolean;
  toggle: () => void;
  resultData: IImportAgency | IImportEmployee | IImportProspect | null;
  loading: boolean;
  refetchData: () => void;
  importScreenType: IMPORT_SCREEN_TYPE;
};

const ImportResultUploadModal: React.FC<ImportResultUploadModalProps> = ({
  isOpen,
  toggle,
  resultData,
  loading,
  refetchData,
  importScreenType,
}) => {
  const { messages, formatMessage } = useTypedIntl();

  const [isValidated, setIsValidated] = useState(false);

  const { run: saveImportAgencyById, loading: saveImportAgencyByIdLoading } =
    useSaveImportAgencyById();

  const {
    run: saveImportEmployeeById,
    loading: saveImportEmployeeByIdLoading,
  } = useSaveImportEmployeeById();

  const {
    run: saveImportProspectById,
    loading: saveImportProspectByIdLoading,
  } = useSaveImportProspectById();

  useEffect(() => {
    if (isOpen) {
      setIsValidated(false);
    }
  }, [isOpen]);

  const {
    data = [],
    fileName,
    fileSize,
    totalRecords = 0,
    headersImportFile,
    isCorrectAllRecords,
    id,
    dataChecked = [],
  } = resultData || {};

  const columns: ColumnsType<any> = useMemo(() => {
    if (!isValidated) {
      return (
        headersImportFile?.map((columnHeader, index) => ({
          key: `${columnHeader}_${index}`,
          title: <div className="truncate">{columnHeader}</div>,
          dataIndex: index,
          width:
            IMPORT_COLUMNS_WIDTH?.[
              columnHeader as keyof typeof IMPORT_COLUMNS_WIDTH
            ] ?? 200,
        })) ?? []
      );
    } else {
      const validationColumns = [
        ...(headersImportFile ?? []),
        'Result',
        'Message',
      ];

      return (
        validationColumns?.map((columnHeader, index) => ({
          key: `${columnHeader}_${index}`,
          title: columnHeader,
          dataIndex: index,
          width:
            IMPORT_COLUMNS_WIDTH?.[
              columnHeader as keyof typeof IMPORT_COLUMNS_WIDTH
            ] ?? 200,
          render: (value: any, _: any, dataIndex: number) => {
            const record = dataChecked[dataIndex] as any;
            const errorMsg = (record?.listRowsUncorrect || [])
              ?.map((item: any) => item?.errorMessage)
              ?.join('\n');
            const hasError = Boolean(errorMsg);

            let valueRender = value;

            if (columnHeader === 'Message') {
              valueRender = (
                <p
                  className={`whitespace-pre-wrap text-left ${
                    errorMsg ? '!text-red-500' : '!text-green-500'
                  }`}
                >
                  {errorMsg}
                </p>
              );
            }

            if (columnHeader === 'Result') {
              valueRender = (
                <p className={errorMsg ? '!text-red-500' : '!text-green-500'}>
                  {errorMsg
                    ? messages['common.failed']
                    : messages['common.success']}
                </p>
              );
            }

            return (
              <div
                className={`${hasError && isValidated ? '!text-red-500' : ''}`}
              >
                {valueRender}
              </div>
            );
          },
        })) || []
      );
    }
  }, [isValidated, headersImportFile, messages]);

  const dataTable = data.length > 1 ? data?.slice(1) : [];

  const handleSaveImport = async (): Promise<void> => {
    try {
      if (!id) return;
      if (importScreenType === IMPORT_SCREEN_TYPE.AGENCY) {
        const res = await saveImportAgencyById(id);
        if (res.success) {
          showMessage({
            type: 'success',
            content: messages['import.agency.importSuccess'],
          });
          toggle();
          refetchData();
        }
      }
      if (importScreenType === IMPORT_SCREEN_TYPE.EMPLOYEE) {
        const res = await saveImportEmployeeById(id);
        if (res.success) {
          showMessage({
            type: 'info',
            content: messages['import.employee.importInProgress'],
          });
          toggle();
        }
      }
      if (importScreenType === IMPORT_SCREEN_TYPE.PROSPECT) {
        const res = await saveImportProspectById(id);
        if (res.success) {
          showMessage({
            type: 'success',
            content: messages['import.prospect.importSuccess'],
          });
          toggle();
          refetchData();
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleValidate = () => {
    const isExistDuplicatedRow = dataChecked?.some(
      (item) => item?.isDuplicateColumn,
    );
    if (isExistDuplicatedRow) {
      AppConfirmation.open({
        title: messages['common.validate.error'],
        paragraph: messages['import.recordExistOrDuplicated'],
        type: 'warning',
        icon: 'IconWarning',
        okText: messages['common.updateNonDuplicated'],
        onConfirm: () => {
          const dataCheckedCorrect =
            dataChecked?.filter(
              (item) => !item?.isDuplicateColumn && !item?.isUncorrectRow,
            ) || [];
          if (!dataCheckedCorrect?.length) {
            showMessage({
              type: 'error',
              content: messages['import.noValidRecordToImport'],
            });
            return;
          }
          handleSaveImport();
        },
      });
      setIsValidated(true);
      return;
    }

    if (isCorrectAllRecords) {
      AppConfirmation.open({
        title: messages['common.success'],
        paragraph: formatMessage(
          { id: 'import.successMappedFields' },
          { fileName },
        ),
        onConfirm: handleSaveImport,
        type: 'success',
        icon: 'IconCheck',
      });
    } else {
      AppConfirmation.open({
        title: messages['common.validate.error'],
        paragraph: messages['import.importError'],
        type: 'warning',
        icon: 'IconWarning',
        showFooterButton: false,
      });
    }
    setIsValidated(true);
  };

  const tableWidth = useMemo(() => {
    if (
      importScreenType === IMPORT_SCREEN_TYPE.AGENCY ||
      importScreenType === IMPORT_SCREEN_TYPE.EMPLOYEE
    ) {
      return 10000;
    }
    if (importScreenType === IMPORT_SCREEN_TYPE.PROSPECT) {
      return 1000;
    }
    return 2000;
  }, [importScreenType]);

  return (
    <AppModalContainer
      isOpen={isOpen}
      toggle={toggle}
      title={messages['bulkUpdate.modal.fileInformation']}
      width="90%"
      minHeight="0px"
    >
      <div className="flex justify-between mb-4">
        <UploadedFileInformation
          fileName={fileName}
          fileSize={fileSize}
          totalRecords={totalRecords}
        />
        <div>
          <AppButton
            name={messages['common.validate']}
            type="primary"
            onClick={handleValidate}
            loading={
              saveImportAgencyByIdLoading ||
              saveImportEmployeeByIdLoading ||
              saveImportProspectByIdLoading
            }
            disabled={
              loading ||
              saveImportAgencyByIdLoading ||
              saveImportEmployeeByIdLoading ||
              saveImportProspectByIdLoading
            }
          />
        </div>
      </div>

      <AppTableContainer
        data={dataTable}
        loading={loading}
        page={0}
        total={totalRecords}
        pageSize={totalRecords}
        columns={columns}
        scroll={{ x: !isValidated ? tableWidth : tableWidth + 700, y: 500 }}
        {...(totalRecords > 1000 ? { virtual: true } : {})}
      />
    </AppModalContainer>
  );
};

export default ImportResultUploadModal;
